import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useState } from "react"
import { FaEdit } from "react-icons/fa"

import { useUpdateEnrollment } from "@/hooks/api/useEnrollments"
import { usePermissions } from "@/hooks/usePermissions"
import type {
  EditEnrollmentProps,
  EnrollmentFormData,
} from "@/types/enrollment"
import { enrollmentUpdateSchema } from "@/types/enrollment"
import { Button } from "@chakra-ui/react"

import EnrollmentFormDialog from "./EnrollmentFormDialog"

const EditEnrollment = memo(
  ({ enrollment, onSuccess, onCancel }: EditEnrollmentProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const updateEnrollmentMutation = useUpdateEnrollment()
    const { hasPermission } = usePermissions()

    const defaultValues = useMemo<EnrollmentFormData>(
      () => ({
        student_id: enrollment.student_id ?? "",
        class_template_id: enrollment.class_template_id ?? "",
        session_id: enrollment.session_id ?? "",
        status: enrollment.status ?? null,
        notes: enrollment.notes ?? "",
      }),
      [enrollment],
    )

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleSubmit = async (data: EnrollmentFormData) => {
      await updateEnrollmentMutation.mutateAsync({
        enrollmentId: enrollment.id,
        enrollmentData: data,
      })
      onSuccess?.()
    }

    // Don't render if user doesn't have permission
    if (!hasPermission("enrollments:update")) {
      return null
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          aria-label={`Edit enrollment: ${enrollment.id}`}
        >
          <FaEdit fontSize="14px" aria-hidden="true" />
          Edit
        </Button>

        <EnrollmentFormDialog
          isOpen={isOpen}
          title="Edit Enrollment"
          submitLabel="Update Enrollment"
          resolver={zodResolver(enrollmentUpdateSchema)}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onClose={handleClose}
          isSubmitting={updateEnrollmentMutation.isPending}
          loadingText="Updating..."
        />
      </>
    )
  },
)

EditEnrollment.displayName = "EditEnrollment"

export default EditEnrollment
export type { EditEnrollmentProps }
